import { api, API_CONFIG } from './api'
import { getCurrentTenant } from './tenant'
import type { User } from '@/types/auth'

const STAFF_PORTAL_ROLES = new Set(['STAFF', 'TENANT_ADMIN', 'SUPERVISOR', 'MANAGER'])

interface AuthTokens {
  accessToken: string
  refreshToken: string
}

export type { User }

export const AUTH_USER_UPDATED_EVENT = 'auth-user-updated'

function notifyAuthUserUpdated(user: User | null): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<User | null>(AUTH_USER_UPDATED_EVENT, { detail: user }))
}

interface AuthResponse {
  data: {
    user: User
    tokens: AuthTokens
  }
}

export const clearAuthSession = (): void => {
  if (typeof window === 'undefined') return
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('user')
  localStorage.removeItem('customer')
  notifyAuthUserUpdated(null)
}

/**
 * Resolve the correct login page for a user role.
 * Customers and platform admins use /login; staff portal roles use /staff/login.
 */
export function getLoginRedirectPath(role?: string | null): string {
  const normalizedRole = role?.toUpperCase()

  if (normalizedRole === 'CUSTOMER' || normalizedRole === 'PLATFORM_ADMIN') {
    return '/login'
  }

  if (normalizedRole && STAFF_PORTAL_ROLES.has(normalizedRole)) {
    return '/staff/login'
  }

  if (typeof window !== 'undefined') {
    const path = window.location.pathname
    if (
      path.startsWith('/staff') ||
      path.startsWith('/tenant') ||
      path.startsWith('/customers') ||
      path.startsWith('/admin') ||
      path.startsWith('/treatments') ||
      path.startsWith('/spa-packages')
    ) {
      return '/staff/login'
    }
  }

  return '/login'
}

export function buildLoginRedirectUrl(role?: string | null, tenant?: string | null): string {
  const path = getLoginRedirectPath(role)
  const tenantParam = tenant ?? getCurrentTenant()
  return `${path}?tenant=${encodeURIComponent(tenantParam)}`
}

export function redirectToLoginPage(options?: {
  role?: string | null
  clearSession?: boolean
  tenant?: string | null
}): void {
  if (typeof window === 'undefined') return

  const role = options?.role ?? authStorage.getUser()?.role
  if (options?.clearSession !== false) {
    clearAuthSession()
  }

  window.location.href = buildLoginRedirectUrl(role, options?.tenant)
}

export const authStorage = {
  getAccessToken: (): string | null => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('accessToken')
  },

  getRefreshToken: (): string | null => {
    if (typeof window === 'undefined') return null
    return localStorage.getItem('refreshToken')
  },

  setTokens: (tokens: AuthTokens): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem('accessToken', tokens.accessToken)
    localStorage.setItem('refreshToken', tokens.refreshToken)
  },

  setAccessToken: (token: string): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem('accessToken', token)
  },

  clearTokens: (): void => {
    clearAuthSession()
  },

  getUser: (): User | null => {
    if (typeof window === 'undefined') return null
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  },

  getCustomer: (): any | null => {
    if (typeof window === 'undefined') return null
    const customerStr = localStorage.getItem('customer')
    return customerStr ? JSON.parse(customerStr) : null
  },

  setUser: (user: User): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem('user', JSON.stringify(user))
    notifyAuthUserUpdated(user)
  },

  setCustomer: (customer: any): void => {
    if (typeof window === 'undefined') return
    localStorage.setItem('customer', JSON.stringify(customer))
  }
}

let isRefreshing = false
let refreshSubscribers: ((token: string) => void)[] = []

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback)
}

const notifyRefreshSubscribers = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token))
  refreshSubscribers = []
}

export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = authStorage.getRefreshToken()

  if (!refreshToken) {
    console.warn('No refresh token available')
    return null
  }

  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    return new Promise((resolve) => {
      addRefreshSubscriber((token) => {
        resolve(token)
      })
    })
  }

  isRefreshing = true

  try {
    const response = await api.post(API_CONFIG.ENDPOINTS.AUTH.REFRESH, {
      refreshToken
    })

    if (!response.ok) {
      throw new Error('Token refresh failed')
    }

    const result = await response.json()

    if (result.data?.tokens?.accessToken) {
      authStorage.setAccessToken(result.data.tokens.accessToken)

      // Update user info if provided
      if (result.data?.user) {
        authStorage.setUser(result.data.user)
      }

      notifyRefreshSubscribers(result.data.tokens.accessToken)
      return result.data.tokens.accessToken
    }

    throw new Error('No access token in refresh response')
  } catch (error) {
    console.error('Token refresh failed:', error)
    // Clear tokens on refresh failure - user needs to login again
    authStorage.clearTokens()
    notifyRefreshSubscribers('')
    return null
  } finally {
    isRefreshing = false
  }
}

export const login = async (email: string, password: string, loginContext?: 'staff' | 'customer'): Promise<AuthResponse> => {
  const response = await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, {
    email,
    password,
    ...(loginContext && { loginContext })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || 'Login failed')
  }

  const result = await response.json()

  // Store tokens and user info
  if (result.data?.tokens) {
    authStorage.setTokens(result.data.tokens)
  }
  if (result.data?.user) {
    authStorage.setUser(result.data.user)
  }

  return result
}

/**
 * RL-020: best-effort real logout. Hits /auth/logout WITH the access token so the
 * server stamps `tokensValidFrom` and invalidates every outstanding access + refresh
 * token. Used by manual logout and by the automatic session-timeout manager.
 * Never throws and never redirects — the caller clears local state and navigates.
 */
export const invalidateServerSession = async (): Promise<void> => {
  try {
    const token = authStorage.getAccessToken()
    if (!token) return
    const { getTenantHeaders } = await import('./tenant')
    await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.AUTH.LOGOUT), {
      method: 'POST',
      headers: {
        ...API_CONFIG.DEFAULT_OPTIONS.headers,
        Authorization: `Bearer ${token}`,
        ...getTenantHeaders(),
      },
      keepalive: true, // allow completion during navigation
    })
  } catch (error) {
    console.warn('Server session invalidation failed (clearing locally anyway):', error)
  }
}

export const logout = async (): Promise<void> => {
  try {
    await invalidateServerSession()
  } finally {
    // Always clear local storage
    authStorage.clearTokens()
  }
}

export const isAuthenticated = (): boolean => {
  return !!(authStorage.getAccessToken() && authStorage.getUser())
}

export const getCurrentUser = (): User | null => {
  return authStorage.getUser()
}

/**
 * BM-028: Refresh cached user (role / posPermissions) from /auth/me so permission
 * changes apply without a full re-login.
 */
export const refreshCurrentUser = async (): Promise<User | null> => {
  const token = authStorage.getAccessToken()
  if (!token) return null

  try {
    const { getTenantHeaders } = await import('./tenant')
    const response = await fetch(API_CONFIG.buildUrl(API_CONFIG.ENDPOINTS.AUTH.ME), {
      method: 'GET',
      headers: {
        ...API_CONFIG.DEFAULT_OPTIONS.headers,
        Authorization: `Bearer ${token}`,
        ...getTenantHeaders(),
      },
    })

    if (!response.ok) return authStorage.getUser()

    const result = await response.json()
    const profile =
      result.data?.user ||
      result.data?.customer ||
      result.data ||
      result
    if (!profile?.id) return authStorage.getUser()

    const existing = authStorage.getUser()
    const lastLoginAt =
      profile.lastLoginAt ??
      result.data?.user?.lastLoginAt ??
      result.data?.customer?.lastLoginAt ??
      existing?.lastLoginAt
    const merged: User = {
      id: profile.id,
      email: profile.email ?? existing?.email ?? '',
      firstName: profile.firstName ?? existing?.firstName ?? '',
      lastName: profile.lastName ?? existing?.lastName ?? '',
      role: profile.role ?? existing?.role ?? '',
      tenantId: profile.tenantId ?? existing?.tenantId,
      // Prefer server array (including empty) so revoked POS modules clear immediately
      posPermissions: Array.isArray(profile.posPermissions)
        ? profile.posPermissions
        : (existing?.posPermissions ?? []),
      lastLoginAt: lastLoginAt ?? existing?.lastLoginAt,
      profilePicture: profile.profilePicture ?? existing?.profilePicture,
    }

    authStorage.setUser(merged)
    return merged
  } catch (error) {
    console.warn('Failed to refresh current user profile:', error)
    return authStorage.getUser()
  }
}

export const getUserRole = (): string | null => {
  const role = getCurrentUser()?.role
  return role || null
}

export const isPlatformAdmin = (): boolean => {
  return getUserRole() === 'PLATFORM_ADMIN'
}

export const isTenantAdmin = (): boolean => {
  const role = getUserRole()
  return role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN'
}

export const isStaff = (): boolean => {
  const role = getUserRole()
  return role === 'STAFF' || role === 'SUPERVISOR' || role === 'MANAGER' || role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN'
}

/** SD-010 R5: matches the backend's requireSupervisor gate — the roles allowed to assign/modify special-condition tags. */
export const isSupervisorOrAbove = (): boolean => {
  const role = getUserRole()
  return role === 'SUPERVISOR' || role === 'MANAGER' || role === 'TENANT_ADMIN' || role === 'PLATFORM_ADMIN'
}